import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { Roles } from 'enums';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import { useUserData } from 'hooks/use-user-data';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { User } from '../account-management.types';

interface Props {
  open: boolean;
  loading: boolean;
  user: User | null;

  firstName: string;
  lastName: string;
  email: string;
  role: string;
  language: string;

  emailInvalid: boolean;
  emailAlreadyExists: boolean;
  firstNameInvalid: boolean;
  lastNameInvalid: boolean;
  invitingUserInvalid: boolean;

  handleUserInfoChange(field: 'firstName' | 'lastName' | 'email' | 'role' | 'language'): (event: React.ChangeEvent<HTMLInputElement>) => void;

  createUser(): void;

  updateUser(): void;

  handleClose(): void;
}

const CreateAndEditUserDialog: React.FC<Props> = ({
  open,
  loading,
  user,
  firstName,
  lastName,
  email,
  role,
  language,
  emailInvalid,
  emailAlreadyExists,
  firstNameInvalid,
  lastNameInvalid,
  invitingUserInvalid,
  handleUserInfoChange,
  createUser,
  updateUser,
  handleClose,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { role: userRole } = useUserData();

  // Define a base set of roles that are always available.
  const roleValues: OptionType[] = [
    { value: Roles.BASIC, label: t('basic') },
    { value: Roles.MANAGER, label: t('manager') },
  ];

  // If user is Admin, they can also add agency and admin roles.
  if (userRole === Roles.ADMIN || userRole === Roles.OWNER) {
    roleValues.push({ value: Roles.AGENCY, label: t('agencyadmin') });
    roleValues.push({ value: Roles.ADMIN, label: t('admin') });
  }

  if (userRole === Roles.OWNER) {
    roleValues.push({ value: Roles.OWNER, label: t('owner') });
  }

  if (userRole === Roles.AGENCY) {
    roleValues.push({ value: Roles.AGENCY, label: t('agencyadmin') });
  }

  const languageValues: OptionType[] = [
    { value: 'de', label: t('german') },
    { value: 'en', label: t('english') },
  ];

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{user ? t('editUser') : t('createUser')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack direction={'column'} gap={2}>
          <PaiTextField
            label={t('firstName')}
            placeholder={t('enterFirstNameHere')}
            value={firstName}
            error={invitingUserInvalid || firstNameInvalid}
            errorText={invitingUserInvalid ? t('invitingUserLength') : firstNameInvalid ? t('firstNameLength') : undefined}
            onChange={handleUserInfoChange('firstName')}
          />
          <PaiTextField
            label={t('lastName')}
            placeholder={t('enterLastNameHere')}
            value={lastName}
            error={invitingUserInvalid || lastNameInvalid}
            errorText={invitingUserInvalid ? t('invitingUserLength') : lastNameInvalid ? t('lastNameLength') : undefined}
            onChange={handleUserInfoChange('lastName')}
          />
          <PaiTextField
            label={t('emailShort')}
            placeholder={t('enterEmailHere')}
            value={email}
            error={emailAlreadyExists || emailInvalid}
            errorText={emailAlreadyExists ? t('emailAlreadyExists') : emailInvalid ? t('noValidEmailError') : undefined}
            onChange={handleUserInfoChange('email')}
          />
          <Divider sx={{ margin: '12px 0 4px 0' }} />
          <PaiSelect value={role} onChange={handleUserInfoChange('role')} label={t('role')}>
            {roleValues.map((item) => (
              <PaiMenuItem key={item.value} value={item.value} disabled={item.value === Roles.AGENCY && userRole !== Roles.ADMIN}>
                {item.label}
              </PaiMenuItem>
            ))}
          </PaiSelect>
          <PaiSelect value={language} onChange={handleUserInfoChange('language')} label={t('language')}>
            {languageValues.map((item) => (
              <PaiMenuItem key={item.value} value={item.value}>
                {item.label}
              </PaiMenuItem>
            ))}
          </PaiSelect>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'md'} underline={'always'} action={handleClose} />
        <PaiButton
          text={user ? t('save') : t('create')}
          border={'rounded'}
          size={'small'}
          action={user ? updateUser : createUser}
          disabled={firstName.trim() === '' || lastName.trim() === '' || email.trim() === '' || role.trim() === '' || language.trim() === ''}
          loading={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default CreateAndEditUserDialog;
